import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container, Typography, Chip, CircularProgress, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import Navbar from '../components/landing/Navbar';
import { COLORS } from '../utils/colors';
import { API_URL } from '../utils/api';

interface Post {
  id: string;
  category: string;
  postType: 'hosted' | 'external';
  title: string;
  slug: string | null;
  description: string;
  body: string | null;
  bodyFormat: string;
  coverImageUrl: string | null;
  externalUrl: string | null;
  displayDate: string;
}

// Convert stored body to safe HTML. Markdown → marked → sanitize; raw HTML → sanitize.
// DOMPurify strips scripts/handlers, so stored content can never inject script.
const renderBody = (post: Post): string => {
  const raw = post.body || '';
  const html = post.bodyFormat === 'markdown' ? (marked.parse(raw, { breaks: true }) as string) : raw;
  return DOMPurify.sanitize(html);
};

const ResourcePostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/resources/${encodeURIComponent(slug || '')}`);
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.success) setPost(data.resource);
        else setError(data.error || 'Post not found');
      } catch {
        if (!cancelled) setError('Failed to load this post');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: COLORS.paper }}>
      <Navbar />

      <Container maxWidth="md" sx={{ flex: 1, py: { xs: 4, md: 6 } }}>
        <Box
          component="a"
          href="/resources"
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 3,
            color: COLORS.primary, fontWeight: 600, fontSize: '0.9rem',
            textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          All resources
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: COLORS.primary }} />
          </Box>
        ) : error || !post ? (
          <Alert severity="error">{error || 'Post not found'}</Alert>
        ) : (
          <Box component="article">
            {post.coverImageUrl && (
              <Box
                component="img"
                src={post.coverImageUrl}
                alt={post.title}
                sx={{ width: '100%', maxHeight: 420, objectFit: 'cover', borderRadius: 3, mb: 3 }}
              />
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
              <Chip
                label={post.category.charAt(0).toUpperCase() + post.category.slice(1)}
                size="small"
                sx={{ bgcolor: COLORS.pastelTeal, color: COLORS.primary, fontWeight: 600 }}
              />
              {post.displayDate && (
                <Typography variant="caption" sx={{ color: COLORS.inkMuted }}>{post.displayDate}</Typography>
              )}
            </Box>

            <Typography variant="h3" sx={{ fontWeight: 700, color: COLORS.ink, mb: 2, fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
              {post.title}
            </Typography>

            {post.description && (
              <Typography variant="h6" sx={{ fontWeight: 400, color: COLORS.inkMuted, mb: 3, lineHeight: 1.6 }}>
                {post.description}
              </Typography>
            )}

            {/* Sanitized body. Content is cleaned by DOMPurify before render. */}
            <Box
              sx={{
                color: COLORS.inkMuted,
                fontSize: '1.05rem',
                '& h1, & h2, & h3, & h4': { color: COLORS.ink, fontWeight: 700, mt: 3.5, mb: 1.5, lineHeight: 1.3 },
                '& h1': { fontSize: '1.8rem' },
                '& h2': { fontSize: '1.5rem' },
                '& h3': { fontSize: '1.25rem' },
                '& p': { lineHeight: 1.8, mb: 2 },
                '& a': { color: COLORS.primary, textDecoration: 'underline' },
                '& img': { maxWidth: '100%', height: 'auto', borderRadius: 2, my: 2 },
                '& ul, & ol': { pl: 3, mb: 2, '& li': { mb: 0.75, lineHeight: 1.7 } },
                '& blockquote': {
                  borderLeft: `4px solid ${COLORS.primary}`, pl: 2, ml: 0, my: 2,
                  color: COLORS.inkMuted, fontStyle: 'italic',
                },
                '& code': { bgcolor: '#F0EDE6', px: 0.75, py: 0.25, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.9em' },
                '& pre': { bgcolor: COLORS.ink, color: '#fff', p: 2, borderRadius: 2, overflow: 'auto', '& code': { bgcolor: 'transparent', color: 'inherit', p: 0 } },
                '& hr': { border: 'none', borderTop: '1px solid #E6E0D6', my: 4 },
              }}
              dangerouslySetInnerHTML={{ __html: renderBody(post) }}
            />

            {(!post.body || !post.body.trim()) && (
              <Typography variant="body2" sx={{ color: COLORS.inkMuted, fontStyle: 'italic', mt: 2 }}>
                This post has no content yet.
              </Typography>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ResourcePostPage;
